import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import { Close as CloseIcon, Warning as WarningIcon } from '@mui/icons-material';
import React, { useState, useEffect, useMemo } from 'react';
import { PriceVersion } from '../types';

export interface CreateVersionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: CreateVersionData) => Promise<void>;
  manufacturerName: string;
  existingVersions: PriceVersion[];
  isLoading?: boolean;
}

export interface CreateVersionData {
  type: 'empty' | 'copy';
  sourceVersionId?: number;
}

const CreateVersionDialog: React.FC<CreateVersionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  manufacturerName,
  existingVersions,
  isLoading = false,
}) => {
  const [versionType, setVersionType] = useState<'empty' | 'copy'>('copy');
  const [sourceVersionId, setSourceVersionId] = useState<number | ''>('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Calculate latest version and new version number
  const latestVersion = useMemo(() => {
    if (existingVersions.length === 0) return null;
    // Sort by version number descending
    const sorted = [...existingVersions].sort((a, b) => b.versionNumber - a.versionNumber);
    return sorted[0];
  }, [existingVersions]);

  const newVersionNumber = useMemo(() => {
    if (!latestVersion) return 1.0;
    return latestVersion.versionNumber + 1;
  }, [latestVersion]);

  // Get most recent published version for default selection
  const mostRecentPublishedVersion = useMemo(() => {
    const published = existingVersions
      .filter(v => v.status === 'Published')
      .sort((a, b) => b.versionNumber - a.versionNumber);
    return published.length > 0 ? published[0] : null;
  }, [existingVersions]);

  // Get all versions sorted descending for dropdown
  const sortedVersions = useMemo(() => {
    return [...existingVersions].sort((a, b) => b.versionNumber - a.versionNumber);
  }, [existingVersions]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setVersionType('copy');
      // Set default to most recent published version, or first version if no published
      const defaultVersion = mostRecentPublishedVersion || sortedVersions[0];
      setSourceVersionId(defaultVersion?.id || '');
      setIsCreating(false);
      setCreateError(null);
    }
  }, [open, mostRecentPublishedVersion, sortedVersions]);

  const handleConfirm = async () => {
    if (versionType === 'copy' && !sourceVersionId) {
      return; // Should not happen, but safety check
    }

    setIsCreating(true);
    setCreateError(null);
    try {
      await onConfirm({
        type: versionType,
        sourceVersionId: versionType === 'copy' ? Number(sourceVersionId) : undefined,
      });
      // Dialog will be closed by parent component after successful creation
    } catch (error) {
      console.error('Error creating version:', error);
      setCreateError('Failed to create the version. Please try again.');
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setIsCreating(false);
      onClose();
    }
  };

  const formatVersionDisplay = (version: PriceVersion): string => {
    return `Version ${version.versionNumber}.0 (${version.status})`;
  };

  const isRecommendedVersion = (version: PriceVersion): boolean => {
    return mostRecentPublishedVersion?.id === version.id;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isCreating}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" component="div">
              Create New Version
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {manufacturerName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              {latestVersion && (
                <Typography variant="body2" color="text.secondary">
                  Current Latest: {formatVersionDisplay(latestVersion)}
                </Typography>
              )}
              <Typography variant="body2" fontWeight="bold" color="primary">
                → New Version: {newVersionNumber}.0
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={isCreating}
            sx={{
              color: theme => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ mb: 3, fontWeight: 600 }}>
          Select how to create the new version:
        </Typography>

        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={versionType}
            onChange={e => {
              if (!isCreating) {
                setVersionType(e.target.value as 'empty' | 'copy');
              }
            }}
          >
            {/* Copy from Existing Version Option */}
            <FormControlLabel
              value="copy"
              control={<Radio disabled={isCreating} />}
              label={
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Copy from Existing Version
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Start with a copy of all data (bundle costs and BOM hardware) from a previous
                    version
                  </Typography>
                  <FormControl
                    fullWidth
                    size="small"
                    disabled={isCreating || versionType !== 'copy'}
                  >
                    <Select
                      value={sourceVersionId}
                      onChange={e => setSourceVersionId(e.target.value as number)}
                      displayEmpty
                    >
                      {sortedVersions.length === 0 ? (
                        <MenuItem disabled value="">
                          No versions available
                        </MenuItem>
                      ) : (
                        sortedVersions.map(version => (
                          <MenuItem key={version.id} value={version.id}>
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
                            >
                              <span>{formatVersionDisplay(version)}</span>
                              {isRecommendedVersion(version) && (
                                <Chip
                                  label="Recommended"
                                  size="small"
                                  color="success"
                                  sx={{ ml: 'auto', height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 2 }}
            />

            {/* Start with Empty Version Option */}
            <FormControlLabel
              value="empty"
              control={<Radio disabled={isCreating} />}
              label={
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Start with Empty Version
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Create version with no data (you will add bundle costs and BOM hardware
                    manually)
                  </Typography>
                  {versionType === 'empty' && (
                    <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>This option requires manual entry of all data</strong> (bundle costs
                        and BOM hardware). Recommended for new manufacturers or significant product
                        line changes.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </RadioGroup>
        </FormControl>

        {createError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {createError}
          </Alert>
        )}

        {/* Progress Indicator */}
        {isCreating && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {versionType === 'copy' ? 'Copying data...' : 'Creating version...'}
              </Typography>
            </Box>
            <LinearProgress />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isCreating || isLoading || (versionType === 'copy' && !sourceVersionId)}
        >
          {isCreating ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateVersionDialog;
